import React, { useRef, useState, useEffect, useMemo, Fragment } from 'react';

import Grid from '@mui/material/Unstable_Grid2/Grid2';

import FeedIcon from '@mui/icons-material/Feed';
import StopIcon from '@mui/icons-material/Stop';
import InfiniteScroll from 'react-infinite-scroller';
import { PaperMyEdls } from '../../Theme';
import { Typography, Divider, CircularProgress, Box, Button, LinearProgress } from '@mui/material';
import { getStylesPapers } from '../../Theme';
import { capitalizeFirstLetter, normalizePhoneNumber, getFormatShortDateFr, getFormatLongDateFr } from '../../utils';

import _ from 'lodash';

import ToolBar from './Toolbar';
import { filterAndSortEdls } from './utils';

function ViewItem({ edls, countEdls, handleOnClick, typeView, setTypeView, display, hasMoreEdls = true, isLoadingMore = false, onLoadMore, onSearchChange, isSearching = false, noResultsFound = false, isAllEdlsLoaded = true, loadingStats = { loaded: 0, total: 0, currentMonths: '' }, onStopLoading }) {
  const page = useRef(0);
  const hasMoreItems = useRef(true);
  const filterEdls = useRef({});
  const visibleEdls = useRef({});
  const nbVisibleEdls = useRef(0);
  const [searchText, setSearchText] = useState('');
  const [status, setStatus] = useState(['submit', 'waiting', 'assign', 'finish']);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [countRefresh, setCountRefresh] = useState(0);
  const [fixedElementHeight, setFixedElementHeight] = useState(0);

  let dateEdl = null;

  useEffect(() => {
    const handleResize = () => {
      const fixedElement = document.getElementById('fixedElement');
      if (fixedElement) {
        setFixedElementHeight(fixedElement.offsetHeight - 18);
      }
    };
    // Petit délai pour laisser le DOM se mettre à jour après changement de isAllEdlsLoaded
    const timeoutId = setTimeout(handleResize, 50);
    const throttledResizeHandler = _.throttle(handleResize, 200);
    window.addEventListener('resize', throttledResizeHandler);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', throttledResizeHandler);
      throttledResizeHandler.cancel();
    };
  }, [display, countEdls, isAllEdlsLoaded, loadingStats]);

  const handleStatus = (status) => {
    setStatus([...status]);
  };

  const handleTypeView = (typeView) => {
    setTypeView(typeView);
  };

  const handleSearchText = (text) => {
    setSearchText(text);
    // Remonter le texte de recherche au parent pour la recherche Firestore
    if (onSearchChange) {
      onSearchChange(text);
    }
  };

  const handleSort = ([sortBy, sortOrder]) => {
    setSortBy(sortBy);
    setSortOrder(sortOrder);
  };

  //FONCTION DE RAPPEL POUR CHAQUE NOUVELLE PAGE (SCROLL INFINITE)
  function loadMoreEdls() {
    // Éviter les appels en boucle si pas d'éléments à charger
    if (!filterEdls.current || filterEdls.current.length === 0) {
      hasMoreItems.current = false;
      return;
    }

    const startIndex = page.current * 20;
    const endIndex = startIndex + 19;

    // Vérifier qu'il y a des éléments à charger
    if (startIndex >= filterEdls.current.length) {
      hasMoreItems.current = hasMoreEdls;
      // Si on a affiché tous les éléments locaux et qu'il y a potentiellement plus d'EDLs sur le serveur
      if (hasMoreEdls && onLoadMore && !isLoadingMore) {
        onLoadMore();
      }
      return;
    }

    const elementsToLoad = Object.fromEntries(filterEdls.current.slice(startIndex, endIndex + 1));
    visibleEdls.current = { ...visibleEdls.current, ...elementsToLoad };
    nbVisibleEdls.current = Object.keys(visibleEdls.current).length;

    // Vérifier s'il y a encore des éléments locaux à charger
    const hasMoreLocalItems = endIndex < filterEdls.current.length - 1;

    // Si on a affiché tous les éléments locaux et qu'il y a potentiellement plus d'EDLs sur le serveur
    if (!hasMoreLocalItems && hasMoreEdls && onLoadMore && !isLoadingMore) {
      onLoadMore();
    }

    // Il y a plus d'éléments à charger si on a des éléments locaux OU des éléments serveur
    hasMoreItems.current = hasMoreLocalItems || hasMoreEdls;
    page.current = page.current + 1;
    setCountRefresh((prev) => prev + 1);
  }

  //RUN DU RENDER
  useMemo(() => {
    filterEdls.current = filterAndSortEdls(edls, status, sortBy, sortOrder, searchText);
    // Limiter les éléments visibles au nombre d'éléments filtrés ou au nombre actuel si plus petit
    const maxVisible = Math.min(nbVisibleEdls.current || 20, filterEdls.current.length);
    visibleEdls.current = Object.fromEntries(filterEdls.current.slice(0, maxVisible > 0 ? maxVisible : 20));
    nbVisibleEdls.current = Object.keys(visibleEdls.current).length;
    // hasMoreItems est vrai seulement s'il y a plus d'éléments filtrés que d'éléments visibles
    hasMoreItems.current = filterEdls.current.length > nbVisibleEdls.current;
    page.current = nbVisibleEdls.current
      ? parseInt((nbVisibleEdls.current / 20).toFixed(0))
      : 0;
  }, [status, sortBy, sortOrder, searchText, edls]);

  useMemo(() => {
    if (nbVisibleEdls.current > 0 && display === 'none') {
      page.current = 1;
      const elementsToLoad = Object.fromEntries(filterEdls.current.slice(0, 20));
      visibleEdls.current = { ...elementsToLoad };
      nbVisibleEdls.current = Object.keys(visibleEdls.current).length;
      // hasMoreItems est vrai seulement s'il y a plus d'éléments filtrés que visibles
      hasMoreItems.current = filterEdls.current.length > nbVisibleEdls.current;
    }
  }, [display]);

  const getColor = (metadatas) => {
    return getStylesPapers(metadatas.status ?? 'submit');
  };

  return (
    <>
      {/*
            <Grid xs={12} textAlign="center">
              Nb EDL selectionnée : {Object.keys(visibleEdls.current).length}{" "}
              <br />
              Nb EDL TOTAL: {Object.keys(filterEdls.current).length}
              <br />
              Nb TOTAL EDL en base de données : {Object.keys(edls).length}
            </Grid>
          </Grid>
       */}

      <Grid container direction="column">
        <Grid
          id="fixedElement"
          sx={{
            padding: 2,
            marginLeft: 2,
            marginTop: 0,
            position: 'fixed',
            left: { xs: 0, sm: 240 },
            top: 48,
            width: '100%',
            zIndex: 1000,
            backgroundColor: '#f5f5f5',
            flexWrap: 'wrap',
          }}
        >
          <ToolBar
            viewTitle={null}
            setViewTitle={null}
            typeView={typeView}
            handleTypeView={handleTypeView}
            calendarRef={null}
            handleStatus={handleStatus}
            handleSort={handleSort}
            handleSearchText={handleSearchText}
          />
          {/* Indicateur de progression du chargement */}
          {!isAllEdlsLoaded && (
            <Box sx={{
              width: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 280px)' },
              mt: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              pr: 2,
            }}>
              <Box sx={{ flexGrow: 1 }}>
                <LinearProgress
                  variant={loadingStats.total > 0 ? 'determinate' : 'indeterminate'}
                  value={loadingStats.total > 0 ? Math.min((loadingStats.loaded / loadingStats.total) * 100, 100) : 0}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              <Typography variant="body2" sx={{ minWidth: 200, whiteSpace: 'nowrap' }}>
                {loadingStats.total > 0
                  ? `${loadingStats.loaded} / ${loadingStats.total} EDLs${loadingStats.currentMonths ? ` (${loadingStats.currentMonths})` : ''}`
                  : 'Chargement...'}
              </Typography>
              {onStopLoading && (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<StopIcon />}
                  onClick={onStopLoading}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Stop
                </Button>
              )}
            </Box>
          )}
        </Grid>
        <Grid
          style={{
            marginTop: fixedElementHeight,
            marginLeft: '8px',
          }}
        >
          <InfiniteScroll
            pageStart={page.current} // spécifiez à partir de quelle page commencer
            loadMore={loadMoreEdls} // passez la fonction qui charge plus d'éléments
            hasMore={hasMoreItems.current} // indiquez s'il y a plus d'éléments à charger
            loader={
              <Box key={0} display="flex" justifyContent="center" alignItems="center" py={2}>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                <Typography variant="body2">
                  {isSearching ? 'Recherche en cours...' : isLoadingMore ? 'Chargement des EDLs...' : 'Chargement...'}
                </Typography>
              </Box>
            }
          >
            {/* Message "EDL non trouvé" */}
            {noResultsFound && Object.keys(visibleEdls.current).length === 0 && !isSearching && (
              <Box display="flex" justifyContent="center" alignItems="center" py={4} width="100%">
                <Typography variant="body1" color="text.secondary">
                  EDL non trouvé
                </Typography>
              </Box>
            )}
            <Grid container spacing={{ xs: 1, md: 2 }}>
              {Object.entries(visibleEdls.current).map(([key, edl]) => {
                // Vérifier que l'EDL a les propriétés minimales requises
                if (!edl || !edl.manage || !edl.possession || !edl.tenants || !edl.metadatas) {
                  return null;
                }
                const tenant = edl.tenants[0] || {};
                const address = edl.possession.address || {};

                let DateDivider = null;
                const dateFormatee = getFormatLongDateFr(edl.manage.dateEDL);
                if (dateEdl !== dateFormatee) {
                  dateEdl = dateFormatee;
                  DateDivider = (
                    <Grid xs={12} mt={{ xs: 1, md: 2 }}>
                      <Typography variant="body">{dateFormatee}</Typography>
                      <Divider />
                    </Grid>
                  );
                }
                return (
                  <Fragment key={key}>
                    {DateDivider !== null && DateDivider}
                    <Grid xs={12} lg={3}>
                      <PaperMyEdls color={getColor(edl.metadatas)} onClick={() => handleOnClick({ edl })}>
                        <FeedIcon className="icon" />
                        <Typography variant="body" className="title-card">
                          Informations
                        </Typography>
                        <Typography variant="body2">
                          Date : {getFormatShortDateFr(edl.manage.dateEDL)} -{' '}
                          {capitalizeFirstLetter(edl.manage.typeEDL)}
                        </Typography>
                        <Typography variant="body2">
                          {capitalizeFirstLetter(edl.possession.typeHouse)} {edl.possession.typeRent} -
                          {edl.possession.nbRoom} - {edl.possession.area} m2
                        </Typography>
                        <Grid>
                          <Divider />
                        </Grid>
                        <Typography variant="body" className="title-card">
                          Adresse
                        </Typography>
                        <Typography variant="body2" className="cardEdl">
                          {address.street || ''}
                        </Typography>
                        <Typography variant="body2">{address.complement || ''}</Typography>
                        <Typography variant="body2">
                          {address.zipCode || ''} {address.locality || ''}
                        </Typography>
                        <Grid>
                          <Divider />
                        </Grid>
                        <Typography variant="body" className="title-card">
                          Locataire
                        </Typography>
                        <Typography variant="body2">
                          {tenant.civility || ''} {tenant.name || ''} {tenant.surname || ''}
                        </Typography>
                        <Typography variant="body2">
                          Email : {!tenant.email ? 'non indiqué' : tenant.email}
                        </Typography>
                        <Typography variant="body2">
                          Téléphone : {normalizePhoneNumber(tenant.phone || '')}
                        </Typography>
                      </PaperMyEdls>
                    </Grid>
                  </Fragment>
                );
              })}
            </Grid>
          </InfiniteScroll>
        </Grid>
      </Grid>
    </>
  );
}

export default ViewItem;
